import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  isNullOrUndefined,
  isWithinBounds,
  useGetFootprintingDairyPrimaryDataById,
  useMutateFootprintingDairyPrimaryData,
  useTranslation,
} from "utils";

import { DLG_ECM_DIVISOR, DLG_ECM_FAT, DLG_ECM_OFFSET, DLG_ECM_PROTEIN } from "consts";

import { ENUM_FARM_FOOTPRINTING_DAIRY_MILK_PRODUCTION, FARM_FOOTPRINTING_DAIRY_MILK_PRODUCTION } from "models";

import { Card, Col, Flex, Form, FormInstance, Icon, InputNumber, KlimRadioGroup, LazyLoadImage, PageSpin, Row, Tooltip } from "components";

interface DairyGeneralProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

export const MilkYieldForm = ({ primaryDataId, readOnly, farmId, year }: DairyGeneralProps) => {
  const { t } = useTranslation("footprintingDairy");
  const formRef = useRef<FormInstance>(null);

  const [milkProduction, setMilkProduction] = useState<ENUM_FARM_FOOTPRINTING_DAIRY_MILK_PRODUCTION | null>(null);
  const [totalMilkYieldKgPerCow, setTotalMilkYieldKgPerCow] = useState<number | null>(null);
  const [fatContentPct, setFatContentPct] = useState<number | null>(null);
  const [proteinContentPct, setProteinContentPct] = useState<number | null>(null);

  const { data: dairyPrimaryDataQuery, isLoading, isSuccess } = useGetFootprintingDairyPrimaryDataById(primaryDataId ?? "");
  const { mutate: mutateDairyPrimaryData } = useMutateFootprintingDairyPrimaryData(
    farmId,
    year,
    {
      farmId,
      year,
      milkProduction: milkProduction,
      milkYieldKg: totalMilkYieldKgPerCow,
      fatContentPct: fatContentPct,
      proteinContentPct: proteinContentPct,
      isSubmitted: false,
    },
    primaryDataId
  );

  const milkYieldAmountECM = useMemo(
    () =>
      totalMilkYieldKgPerCow && fatContentPct != null && proteinContentPct != null
        ? (totalMilkYieldKgPerCow * (DLG_ECM_FAT * (fatContentPct * 100) + DLG_ECM_PROTEIN * (proteinContentPct * 100) + DLG_ECM_OFFSET)) / DLG_ECM_DIVISOR
        : null,
    [totalMilkYieldKgPerCow, fatContentPct, proteinContentPct]
  );

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateDairyPrimaryData();
      }
    },
    [isLoading, mutateDairyPrimaryData, readOnly]
  );

  useEffect(() => {
    if (dairyPrimaryDataQuery && isSuccess) {
      setMilkProduction(dairyPrimaryDataQuery.milkProduction ?? null);
      setTotalMilkYieldKgPerCow(dairyPrimaryDataQuery.milkYieldKg ?? null);
      setFatContentPct(dairyPrimaryDataQuery.fatContentPct ?? null);
      setProteinContentPct(dairyPrimaryDataQuery.proteinContentPct ?? null);

      formRef.current?.setFieldsValue({
        MilkYieldKgPerCow: dairyPrimaryDataQuery.milkYieldKg ?? null,
        fatContentPct: formatFractionInput(dairyPrimaryDataQuery.fatContentPct ?? null),
        proteinContentPct: formatFractionInput(dairyPrimaryDataQuery.proteinContentPct ?? null),
      });

      const fieldsToValidate = (["milkYieldKg", "fatContentPct", "proteinContentPct"] as const).some((field) => dairyPrimaryDataQuery[field] != null);

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [dairyPrimaryDataQuery, isSuccess]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <div>
      <h1 className="center margin-bottom-200 padding-left-400 padding-right-400 margin-left-100 margin-right-100">{t("dairyMilkYieldForm.title")}</h1>
      <Card className="padding-100 margin-bottom-100">
        <Flex className="padding-100" gap="1rem" vertical>
          <LazyLoadImage alt={"/images/onboarding/farm.jpg"} aria-label="farm-logo-image" src={"/images/onboarding/dairy.webp"} />
        </Flex>
        <Flex className="padding-100" gap="1rem" vertical>
          <p className="paragraph-m margin-0">{t("dairyMilkYieldForm.info")}</p>
        </Flex>
      </Card>
      <Card dataTestId="footprinting-farm-soil-form" className="margin-bottom-100 padding-200">
        <Flex vertical gap={4}>
          <h3>{t("dairyMilkYieldForm.milkYieldQuestion")}</h3>
          <p className="margin-top-0 margin-bottom-250 grey-600-text paragraph-m">{t("dairyMilkYieldForm.milkYieldQuestionDescription")}</p>
        </Flex>
        <Form ref={formRef}>
          <Flex vertical gap={24}>
            <Flex align="center" gap={16} justify="space-between">
              <span style={{ maxWidth: "50%" }}>
                {t("dairyMilkYieldForm.question.milkYieldRadioQuestion")}
                {true && <span className="required-asterisk">*</span>}
                <Tooltip title={"tool tip coming soon"}>
                  <Icon size="s" iconName="tabler/info-circle" className="input-label-info-icon" />
                </Tooltip>
              </span>
              <KlimRadioGroup
                required
                vertical={false}
                disabled={readOnly}
                type="tertiary"
                size="large"
                dataTestId="footprinting-dairy-selfProducted-delivered-radio"
                onChange={({ target }) => {
                  setMilkProduction(target.value);
                }}
                value={milkProduction}
                optionType="button"
                options={[FARM_FOOTPRINTING_DAIRY_MILK_PRODUCTION.PRODUCED, FARM_FOOTPRINTING_DAIRY_MILK_PRODUCTION.DELIVERED].map((milkProduction) => ({
                  label: t(`dairyMilkYieldForm.milkYieldProductionOptions.${milkProduction}`),
                  value: milkProduction,
                }))}
              />
            </Flex>
            <Form.Item>
              <InputNumber
                disabled={readOnly}
                name="MilkYieldKgPerCow"
                dataTestId="footprinting-dairy-milk-yield-weight-input"
                label={t("dairyMilkYieldForm.question.milkYieldAmountLabel")}
                suffix={t("dairyMilkYieldForm.question.milkYieldAmountSuffix")}
                value={totalMilkYieldKgPerCow}
                initialValue={totalMilkYieldKgPerCow}
                onChange={(v) => setTotalMilkYieldKgPerCow(v)}
                validateFirst
                rules={[
                  { required: true, message: t("dairyMilkYieldForm.error.milkYieldAmount") },
                  { type: "number" },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isWithinBounds(v, 1, 16000)) {
                        return Promise.reject(new Error(t("dairyMilkYieldForm.error.milkYieldAmount")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </Form.Item>
            <Row gutter={0} className="padding-bottom-0">
              <Col xs={24} sm={12}>
                <Form.Item className="padding-right-50">
                  <InputNumber
                    className="floating-label"
                    dataTestId={"footprinting-farm-dairy-milk-yield-fat-content-input"}
                    disabled={readOnly}
                    name={"fatContentPct"}
                    label={t("dairyMilkYieldForm.question.fatContentLabel")}
                    suffix="%"
                    value={formatFractionInput(fatContentPct)}
                    initialValue={formatFractionInput(fatContentPct)}
                    onChange={(v) => setFatContentPct(formatPercentageInput(v))}
                    precision={2}
                    validateFirst
                    rules={[
                      { required: true, type: "number", message: t("dairyMilkYieldForm.error.fatContentValue") },
                      {
                        validator: (_: unknown, v: number) => {
                          if (isNullOrUndefined(v) || !isWithinBounds(v, 2, 8)) {
                            return Promise.reject(new Error(t("dairyMilkYieldForm.error.fatContentValue")));
                          }

                          return Promise.resolve();
                        },
                      },
                    ]}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item className="padding-left-50">
                  <InputNumber
                    className="floating-label"
                    dataTestId={"footprinting-farm-dairy-milk-yield-protein-content-input"}
                    disabled={readOnly}
                    name={"proteinContentPct"}
                    label={t("dairyMilkYieldForm.question.proteinContentLabel")}
                    suffix="%"
                    value={formatFractionInput(proteinContentPct)}
                    initialValue={formatFractionInput(proteinContentPct)}
                    onChange={(v) => setProteinContentPct(formatPercentageInput(v))}
                    precision={2}
                    validateFirst
                    rules={[
                      { required: true, type: "number", message: t("dairyMilkYieldForm.error.proteinContentValue") },
                      {
                        validator: (_: unknown, v: number) => {
                          if (isNullOrUndefined(v) || !isWithinBounds(v, 2.5, 6)) {
                            return Promise.reject(new Error(t("dairyMilkYieldForm.error.proteinContentValue")));
                          }

                          return Promise.resolve();
                        },
                      },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Card className="dairy-display-information-card padding-100 flex-center">
              <p className="paragraph-s grey-600-text margin-0 margin-bottom-25">{t("dairyMilkYieldForm.milkYieldDisplayTitle")}</p>
              <h5 className="center">{milkYieldAmountECM !== null ? `${milkYieldAmountECM.toFixed(0)} kg` : "0 kg"}</h5>
            </Card>
          </Flex>
        </Form>
      </Card>
    </div>
  );
};
